import Component from "@ember/component"
import { action, computed, get, set } from "@ember/object"
import { inject as service } from "@ember/service"
import { handleError } from "cc-frontend/error-handling"
import prepareSetUserLastPrintOptions from "cc-frontend/lib/actions/user/SET_USERS_LAST_PRINT_OPTIONS"
import _ from "lodash"
import moment from "moment"
import { payloadForComponent } from "../../../lib/action-creators/app-state"
import registerComponent from "../../../mixins/register-component"

export default class ConfigurePrintDownload extends Component.extend(registerComponent) {
  componentId = "print-configurator"

  @service("dispatcher")
  dispatcher

  @service
  rpc

  @service
  router

  @service
  session

  @service
  appState

  @service
  modalManager

  init() {
    super.init(...arguments)
    this.defaultState = {
      settings: {
        perspective: "",
        params: {},
        options: {
          headerText: "",
          fontSize: "10",
          showComments: false,
          orientation: "landscape",
          isFilteringCards: false,
          showStandardDescriptions: true,
          pageBreaks: "none",
          visibleCards: [],
        },
        output: {
          medium: "print",
          fileType: "pdf",
          fileName: "",
        },
        email: {
          to: "",
          body: "",
          subject: "",
        },
      },
    }
    this.printTokenPromise = new Promise((resolve) =>
      get(this, "rpc")
        .execute("GET_PRINT_TOKEN")
        .then((token) => resolve(token))
        .catch((_error) => {
          get(this, "dispatcher").dispatch("CLOSE_PRINT_MODAL", {
            sessionId: get(this, "appState.id"),
            componentId: "print-modal",
          })

          swal({
            title: `Whoops! Looks like your internet connection might be down.`,
            text: `Check your connection and try again.`,
            type: "warning",
          }).catch((error) => {
            if (error instanceof Error) handleError(error)
          })
        })
    )
  }

  generateParams(layout) {
    switch (layout) {
      case "course-date:single":
        return this.getProperties(["courseId", "startDate", "endDate"])
      case "course-date:range":
        return this.getProperties(["courseId", "startDate", "endDate"])

      case "planbook-date:single":
        return this.getProperties([
          "planbookId",
          "startDate",
          "endDate",
          "isFilteringClasses",
          "courseIds",
          "classesLinedUp",
        ])

      case "planbook-date:range:full-page":
        return this.getProperties([
          "planbookId",
          "startDate",
          "endDate",
          "isFilteringClasses",
          "courseIds",
          "classesLinedUp",
        ])
      case "planbook-date:range:columns":
        return this.getProperties([
          "planbookId",
          "startDate",
          "endDate",
          "isFilteringClasses",
          "courseIds",
          "classesLinedUp",
        ])

      case "planbook-month:grid":
        return this.getProperties([
          "planbookId",
          "startDate",
          "endDate",
          "isFilteringClasses",
          "courseIds",
          "classesLinedUp",
        ])

      case "course-outline":
        return this.getProperties([
          "courseId",
          "startDate",
          "endDate",
          "visibleCards",
          "printUnitLessons",
        ])

      case "course-unit":
        return this.getProperties(["courseId", "startDate", "endDate", "unitId"])

      case "course-units":
        return this.getProperties(["courseId"])

      case "planbook-standards":
        return this.getProperties([
          "planbookId",
          "courseIds",
          "standardSetId",
          "showStandardsNotCovered",
        ])
    }
  }

  @computed("state.settings.perspective")
  get validOptions() {
    let layout = get(this, "state.settings.perspective")
    if (layout === undefined) return []
    let globalOptions = ["headerText", "fontSize", "orientation"]
    let opts = {
      "course-date:single": [
        "showStandardDescriptions",
        "showComments",
        "isFilteringCards",
        "visibleCards",
      ],
      "course-date:range": [
        "showStandardDescriptions",
        "showComments",
        "isFilteringCards",
        "visibleCards",
        "pageBreaks",
      ],
      "planbook-date:single": [
        "showStandardDescriptions",
        "showComments",
        "isFilteringCards",
        "visibleCards",
        "pageBreaks",
      ],
      "planbook-date:range:full-page": [
        "showStandardDescriptions",
        "showComments",
        "isFilteringCards",
        "visibleCards",
        "pageBreaks",
      ],
      "planbook-date:range:columns": [
        "showStandardDescriptions",
        "showComments",
        "isFilteringCards",
        "visibleCards",
        "pageBreaks",
      ],
      "planbook-month:grid": [],
      "course-outline": [
        "showStandardDescriptions",
        "showComments",
        "visibleCards",
        "printUnitLessons",
      ],
      "course-unit": [
        "showStandardDescriptions",
        "showComments",
        "isFilteringCards",
        "visibleCards",
        "pageBreaks",
        "shouldPrintOutline",
        "printUnitLessons",
      ],
      "course-units": [],
      "planbook-standards": [],
    }
    return opts[layout].concat(globalOptions)
  }

  didReceiveAttrs() {
    super.didReceiveAttrs(...arguments)

    // if (this.hasSetSettings === true) return;
    // this.hasSetSettings = true

    // "course-date", "planbook-date", "planbook-month", "course-outline"
    let printOpts = get(this, "lastPrintOptions") || {}
    let lastPrintOptions = printOpts[get(this, "perspective")]

    let initialSettings = {
      perspective: get(this, "state.settings.perspective") || get(this, "perspective"),
      params: this.generateParams(get(this, "perspective")),
      options: _.merge(
        {
          headerText: get(this, "fullName") + " " + get(this, "planbookTitle"),
          fontSize: "10",
          showStandardDescriptions: true,
          showComments: false,
          orientation: "portrait",
          isFilteringCards: false,
          pageBreaks: "none",
          visibleCards: [],
        },
        lastPrintOptions
      ),
      output: {
        medium: get(this, "medium") || "print",
        fileType: "pdf",
        fileName: get(this, "fullName") + " -- " + moment().format("YYYY-MM-DD -- dddd"),
      },
      email: {
        to: "",
        body: "",
        subject: "",
      },
    }

    set(this, "state.settings", initialSettings)
  }

  @computed("state.settings.perspective")
  get showLayoutSelector() {
    return _.includes(
      ["planbook-date:range:full-page", "planbook-date:range:columns"],
      get(this, "state.settings.perspective")
    )
  }

  @computed("validOptions.[]")
  get showCardSelector() {
    return _.includes(get(this, "validOptions"), "isFilteringCards")
  }

  @computed("validOptions.[]")
  get showCommentsSelector() {
    return _.includes(get(this, "validOptions"), "showComments")
  }

  @computed("validOptions.[]")
  get showStandardDescriptionsSelector() {
    return _.includes(get(this, "validOptions"), "showStandardDescriptions")
  }

  @computed("validOptions.[]")
  get showPageBreakSelector() {
    return _.includes(get(this, "validOptions"), "pageBreaks")
  }

  @computed("validOptions.[]")
  get showPrintOutlineSelector() {
    return _.includes(get(this, "validOptions"), "shouldPrintOutline")
  }

  @computed("validOptions.[]")
  get showPrintUnitLessonsSelector() {
    return _.includes(get(this, "validOptions"), "printUnitLessons")
  }

  @computed("state.settings.perspective")
  get pageBreakOptions() {
    let opts = {
      "course-date:range": ["none", "after-class"],
      "planbook-date:single": ["none", "after-class"],
      "planbook-date:range:full-page": ["none", "after-class", "after-day"],
      "planbook-date:range:columns": ["none", "after-class"],
      "course-unit": ["none", "after-lesson"],
    }

    let labels = {
      "after-lesson": "After Each Lesson",
      "after-class": "After Each Class",
      "after-day": "After Each Day",
      none: "None",
    }

    let validOpts = opts[get(this, "state.settings.perspective")]
    if (validOpts) {
      return _.map(validOpts, (id) => {
        return {
          value: id,
          label: labels[id],
        }
      })
    } else {
      return false
    }
  }

  @action
  setOption(option, value) {
    value = value === "true" ? true : value
    value = value === "false" ? false : value
    get(this, "dispatcher").dispatch(
      "PRINT_CONFIGURATOR_SET_OPTION",
      _.merge({}, payloadForComponent(get(this, "appState"), "print-configurator"), {
        option: option,
        value: value,
      })
    )
  }

  @action
  setLayout(perspective) {
    set(this, "state.settings.perspective", perspective)
    // get(this, 'dispatcher').dispatch("PRINT_CONFIGURATOR_SET_LAYOUT", _.merge({},
    //   payloadForComponent(get(this, 'appState'), "print-configurator"), {
    //     perspective: perspective
    //   })
    // )
  }

  @action
  setMedium(medium) {
    get(this, "dispatcher").dispatch(
      "PRINT_CONFIGURATOR_SET_MEDIUM",
      _.merge({}, payloadForComponent(get(this, "appState"), "print-configurator"), {
        medium: medium,
      })
    )
  }

  @action
  changeFileName(fileName) {
    get(this, "dispatcher").dispatch(
      "PRINT_CONFIGURATOR_SET_FILE_NAME",
      _.merge({}, payloadForComponent(get(this, "appState"), "print-configurator"), {
        fileName: fileName,
      })
    )
  }

  @action
  async execute(medium) {
    // Guard against the user re-clicking while we await the printToken.
    if (this.isExecuting) return
    set(this, "isExecuting", true)

    let params = get(this, "state.settings.params")
    let options = _.pick(get(this, "state.settings.options"), get(this, "validOptions"))

    const printToken = await this.printTokenPromise
    // Stop if the user gave up and closed the modal.
    if (this.isDestroyed || this.isDestroying) return

    // You can't do page breaks after a class if classes aren't lined up
    if (options.pageBreaks === "after-class") {
      params.classesLinedUp = true
    }
    let allQueryParams = Object.assign({}, params, options, {
      printToken,
      medium: medium,
      fileName: get(this, "state.settings.output.fileName"),
      showStandardDescriptions:
        get(this, "state.settings.options.showStandardDescriptions") === true,
    })

    if (get(this, "state.settings.perspective") === "planbook-date:range:columns") {
      allQueryParams["asColumns"] = true
    }

    let queryString = _.chain(allQueryParams)
      .reduce((acc, val, key) => {
        let value = val
        value = key === "visibleCards" ? _.split(value, ",") : value
        value = _.isArray(value) ? JSON.stringify(value) : value
        acc.push(encodeURIComponent(key) + "=" + encodeURIComponent(value))
        return acc
      }, [])
      .join("&")
      .value()

    let perspectiveToEndpoint = {
      "course-date:single": "course-date",
      "course-date:range": "course-date",
      "planbook-date:single": "planbook-date",
      "planbook-date:range:full-page": "planbook-date",
      "planbook-date:range:columns": "planbook-date",
      "planbook-month:grid": "planbook-month",
      "course-outline": "course-outline",
      "course-unit": "course-unit",
      "course-units": "course-units",
      "planbook-standards": "planbook-standards",
    }
    let perspectiveToResourceIdField = {
      "course-date:single": "courseId",
      "course-date:range": "courseId",
      "planbook-date:single": "planbookId",
      "planbook-date:range:full-page": "planbookId",
      "planbook-date:range:columns": "planbookId",
      "planbook-month:grid": "planbookId",
      "course-outline": "courseId",
      "course-unit": "courseId",
      "course-units": "courseId",
      "planbook-standards": "planbookId",
    }

    let resourceIdField = perspectiveToResourceIdField[get(this, "state.settings.perspective")]
    let id = params[resourceIdField]

    let endpoint = perspectiveToEndpoint[get(this, "state.settings.perspective")]
    window.open(`/api/v4/print/${endpoint}/${id}?${queryString}`, "_blank")

    if (get(this, "session.isAuthenticated")) {
      this.dispatcher.dispatchPreparedAction(
        prepareSetUserLastPrintOptions({
          planbookId: get(this, "planbookId"),
          currentUserId: get(this, "session.userId"),
          settingsToSave: get(this, "state.settings.options"),
          perspective: get(this, "state.settings.perspective"),
          contextForAnalytics: {
            perspective: get(this, "state.settings.perspective"),
            medium: medium,
            showStandardDescriptions: allQueryParams.showStandardDescriptions,
            routeName: this.router.currentRoute.name,
            ...options,
          },
        })
      )
    }

    this.modalManager.closeModal("planner/modals/print-download")
  }
}
